'use client';

import type {
  ManualAnalyticsCoordinator,
  ManualAnalyticsHookTarget,
  SensorsEnvelope,
  SensorsRuntime,
  SensorsStage,
  SensorsStageContext,
} from './manualAnalyticsTypes';

/** 来源标记仅用于本站同步业务范围，必须在组装前移除。 */
export const MANUAL_OCCURRENCE_PROPERTY = '__nextmar_manual_occurrence';
/** 私有拥有者证明，不在业务对象上安装可写钩子。 */
const targets = new WeakMap<ManualAnalyticsCoordinator, ManualAnalyticsHookTarget>();
/** 同一真实 SDK 只能安装一个来源拥有者；回入安装幂等。 */
const installations = new WeakMap<SensorsRuntime, ManualAnalyticsCoordinator>();

/**
 * 绑定工厂创建的内部处理器。
 * @param coordinator 本文档的协调器。
 * @param target 私有事件关联与发送处理。
 * @returns 无返回值，不创建第二条队列。
 */
export function bindSensorsMirrorHookTarget(
  coordinator: ManualAnalyticsCoordinator,
  target: ManualAnalyticsHookTarget,
): void {
  targets.set(coordinator, target);
}

/**
 * 在 Sensors init 前安装关联与发送 gate。
 * @param sensors 同一窗口的实际 SDK，不接受 bootstrap 替身。
 * @param coordinator 已绑定的本站协调器。
 * @returns 无返回值；API 缺失或所有权冲突明确抛错，由调用方诊断。
 */
export function installSensorsMirrorHooks(
  sensors: SensorsRuntime,
  coordinator: ManualAnalyticsCoordinator,
): void {
  const installed = installations.get(sensors);
  if (installed === coordinator) return;
  if (installed) throw new Error('manual_hooks_owner_conflict');
  const target = targets.get(coordinator);
  if (
    !target ||
    typeof sensors.registerFeature !== 'function' ||
    typeof sensors.registerInterceptor !== 'function' ||
    typeof sensors.on !== 'function'
  )
    throw new Error('manual_hooks_unavailable');
  let stage: SensorsStage | undefined;
  /** 继续原 envelope 时只能跳过自己的这一次 gate。 */
  const resuming = new WeakSet<SensorsEnvelope>();
  /** 再次重入同一个已继续 envelope 时阻止重复 SDK 交接。 */
  const continued = new WeakSet<SensorsEnvelope>();
  /** 官方 Stage 会捕获回调异常；使用本地标记把继续失败传回拥有者。 */
  let resumeError = false;
  sensors.registerFeature({
    sendDataStage: {
      init(value) {
        stage = value;
      },
    },
  });
  if (!stage || typeof stage.process !== 'function')
    throw new Error('manual_send_stage_unavailable');
  sensors.registerInterceptor('buildDataStage', {
    finalAdjustData: {
      priority: -100,
      entry(data) {
        target.associate(data);
        return data;
      },
    },
  });
  sensors.on('data:assembled', (data) => target.observe(data));
  sensors.registerInterceptor('sendDataStage', {
    send: {
      priority: -1000,
      /** @param envelope 原发送对象。@param context 官方取消和位置上下文。@returns 同一对象，不克隆官方配置。 */
      entry(envelope: SensorsEnvelope, context: SensorsStageContext) {
        if (resuming.has(envelope)) {
          resuming.delete(envelope);
          if (context.getPosition().current !== 1) {
            resumeError = true;
            context.cancellationToken.stop();
          }
          return envelope;
        }
        if (continued.has(envelope)) {
          context.cancellationToken.stop();
          return envelope;
        }
        target.pause(envelope, context, () => {
          if (continued.has(envelope)) return;
          continued.add(envelope);
          resuming.add(envelope);
          resumeError = false;
          try {
            stage!.process('send', envelope);
            if (resumeError) throw new Error('send_interceptor_order');
          } finally {
            resuming.delete(envelope);
          }
        });
        return envelope;
      },
    },
  });
  installations.set(sensors, coordinator);
}
